import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { MapPin, IndianRupee, Briefcase, ArrowLeft, CheckCircle, Loader2, Phone, User, Award, FileText, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApply, setShowApply] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', experience: '', license_type: '' });
  const [resumeFile, setResumeFile] = useState(null);

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const res = await axios.get(`${API}/jobs/${id}`);
        setJob(res.data);
      } catch { toast.error('Job not found'); navigate('/driver-jobs'); }
      finally { setLoading(false); }
    };
    fetchJob();
  }, [id]);

  const handleApply = async (e) => {
    e.preventDefault();
    setApplying(true);
    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('phone', form.phone);
      formData.append('experience', form.experience);
      formData.append('license_type', form.license_type);
      if (resumeFile) {
        formData.append('resume', resumeFile);
      }
      await axios.post(`${API}/jobs/${id}/apply`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setApplied(true);
      setShowApply(false);
      toast.success('Application submitted!');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to apply'); }
    finally { setApplying(false); }
  };

  const whatsappMessage = job ? `Hi, I'm interested in the "${job.title}" position at PrimeSwift (${job.location}). My details:\nName: \nPhone: \nExperience: \nLicense Type: ` : '';
  const whatsappUrl = `https://wa.me/919579027425?text=${encodeURIComponent(whatsappMessage)}`;

  const renderList = (text) => {
    if (!text) return null;
    return text.split('\n').filter(Boolean).map((item, i) => (
      <li key={i} className="flex items-start space-x-2 py-1">
        <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
        <span className="text-slate-700">{item.replace(/^[-*•]\s*/, '')}</span>
      </li>
    ));
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!job) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
          <Button data-testid="back-to-jobs" variant="ghost" size="sm" onClick={() => navigate('/driver-jobs')} className="text-white hover:bg-white/10 -ml-2 mb-4"><ArrowLeft className="w-4 h-4 mr-1" />All Careers</Button>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold mb-3" data-testid="job-title">{job.title}</h1>
              <div className="flex flex-wrap items-center gap-3 text-slate-300">
                <Badge className={job.job_type === 'full-time' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : 'bg-blue-500/20 text-blue-300 border-blue-500/30'}>{job.job_type === 'full-time' ? 'Full-time' : 'Part-time'}</Badge>
                <span className="flex items-center space-x-1"><MapPin className="w-4 h-4" /><span>{job.location}</span></span>
                <span className="flex items-center space-x-1"><IndianRupee className="w-4 h-4" /><span>{job.salary_range}</span></span>
              </div>
            </div>
            {!applied && job.status === 'active' && (
              <Button data-testid="apply-now-button" onClick={() => setShowApply(true)} className="bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full h-12 px-8 text-base whitespace-nowrap">Apply Now</Button>
            )}
            {applied && (
              <div className="flex items-center space-x-2 bg-emerald-500/20 px-4 py-2 rounded-full">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <span className="text-emerald-300 font-semibold">Applied!</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Description */}
        <Card className="border-slate-100 shadow-sm">
          <CardHeader><CardTitle className="flex items-center space-x-2"><Briefcase className="w-5 h-5" /><span>Job Description</span></CardTitle></CardHeader>
          <CardContent><p className="text-slate-700 leading-relaxed whitespace-pre-line">{job.description}</p></CardContent>
        </Card>

        {/* Responsibilities */}
        {job.responsibilities && (
          <Card className="border-slate-100 shadow-sm">
            <CardHeader><CardTitle>Responsibilities</CardTitle></CardHeader>
            <CardContent><ul className="space-y-1">{renderList(job.responsibilities)}</ul></CardContent>
          </Card>
        )}

        {/* Requirements */}
        {job.requirements && (
          <Card className="border-slate-100 shadow-sm">
            <CardHeader><CardTitle>Requirements</CardTitle></CardHeader>
            <CardContent><ul className="space-y-1">{renderList(job.requirements)}</ul></CardContent>
          </Card>
        )}

        {/* Benefits */}
        {job.benefits && (
          <Card className="border-slate-100 shadow-sm">
            <CardHeader><CardTitle>Benefits</CardTitle></CardHeader>
            <CardContent><ul className="space-y-1">{renderList(job.benefits)}</ul></CardContent>
          </Card>
        )}

        {/* CTA */}
        {!applied && job.status === 'active' && (
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button data-testid="apply-bottom-button" onClick={() => setShowApply(true)} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full h-14 text-lg">Apply Now</Button>
            <Button data-testid="whatsapp-apply" onClick={() => window.open(whatsappUrl, '_blank')} variant="outline" className="flex-1 border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-bold rounded-full h-14 text-lg">Apply via WhatsApp</Button>
          </div>
        )}
      </div>

      {/* Apply Modal */}
      <Dialog open={showApply} onOpenChange={setShowApply}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Apply for {job.title}</DialogTitle><DialogDescription>{job.location} - {job.salary_range}</DialogDescription></DialogHeader>
          <form onSubmit={handleApply} className="space-y-4">
            <div className="space-y-2"><Label>Full Name *</Label><div className="relative"><User className="absolute left-3 top-3 h-5 w-5 text-slate-400" /><Input data-testid="apply-name" placeholder="Enter full name" className="pl-10 h-12 bg-slate-50" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required /></div></div>
            <div className="space-y-2"><Label>Phone Number *</Label><div className="relative"><Phone className="absolute left-3 top-3 h-5 w-5 text-slate-400" /><Input data-testid="apply-phone" type="tel" placeholder="10-digit mobile" className="pl-10 h-12 bg-slate-50" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} required /></div></div>
            <div className="space-y-2"><Label>Driving Experience *</Label><div className="relative"><Award className="absolute left-3 top-3 h-5 w-5 text-slate-400" /><Input data-testid="apply-experience" placeholder="e.g., 3 years" className="pl-10 h-12 bg-slate-50" value={form.experience} onChange={(e) => setForm({...form, experience: e.target.value})} required /></div></div>
            <div className="space-y-2"><Label>License Type *</Label>
              <Select value={form.license_type} onValueChange={(v) => setForm({...form, license_type: v})}>
                <SelectTrigger data-testid="apply-license" className="h-12 bg-slate-50"><SelectValue placeholder="Select license type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="LMV">LMV (Light Motor Vehicle)</SelectItem>
                  <SelectItem value="HMV">HMV (Heavy Motor Vehicle)</SelectItem>
                  <SelectItem value="HGMV">HGMV (Heavy Goods Motor Vehicle)</SelectItem>
                  <SelectItem value="Transport">Transport Vehicle</SelectItem>
                  <SelectItem value="Two Wheeler">Two Wheeler</SelectItem>
                  <SelectItem value="Three Wheeler">Three Wheeler</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Resume / Biodata</Label>
              <div className="relative">
                <input
                  data-testid="apply-resume"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setResumeFile(e.target.files[0] || null)}
                  className="hidden"
                  id="resume-upload"
                />
                <label
                  htmlFor="resume-upload"
                  className="flex items-center gap-3 px-4 h-12 bg-slate-50 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-100 transition-colors"
                >
                  <Upload className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  <span className="text-sm text-slate-600 truncate">
                    {resumeFile ? resumeFile.name : 'Upload PDF, DOC, DOCX (max 5MB)'}
                  </span>
                </label>
              </div>
            </div>
            <Button type="submit" data-testid="submit-application" className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-full h-12 font-bold text-base" disabled={applying || !form.license_type}>{applying ? 'Submitting...' : 'Submit Application'}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobDetails;
